type Properties = { [key: string]: unknown };

// These have carried customer content (warehouse errors, model and connection names), which Mixpanel must not receive.
const BLOCKED_PROPERTIES = [
  "error",
  "error_message",
  "model_name",
  "connection",
  "label",
  "reason",
];

const URL_PROPERTIES = ["$current_url", "$referrer", "$initial_referrer"];

const REDACTED = "redacted";

// Query values that only hold Weld IDs or fixed options; a field list keeps just those fields of a JSON value.
const KEPT_QUERY_VALUES = new Map<string, true | string[]>([
  ["accountId", true],
  ["assistant", true],
  ["assistantThread", true],
  ["authFlowSuccess", true],
  ["checkout", true],
  ["connection_id", true],
  ["dashboardId", true],
  ["integrationId", true],
  ["isWeldBQ", true],
  ["location", true],
  ["payment_checkout", true],
  ["resourceId", true],
  ["source", true],
  ["source_id", true],
  ["source_type", true],
  ["success", true],
  ["tileId", true],
  ["utm_campaign", true],
  ["utm_medium", true],
  ["utm_source", true],
  ["v", true],
  ["viewDataSource", ["syncId", "tab", "run"]],
  ["viewDataSourceTab", true],
  ["workflowRunId", true],
]);

function parseJsonObject(value: string) {
  try {
    const parsed: unknown = JSON.parse(value);
    return parsed !== null &&
      typeof parsed === "object" &&
      !Array.isArray(parsed)
      ? (parsed as Properties)
      : null;
  } catch {
    return null;
  }
}

function redactQueryValue(key: string, value: string) {
  const kept = KEPT_QUERY_VALUES.get(key);
  if (kept === true) return value;
  const json = kept ? parseJsonObject(value) : null;
  if (!kept || !json) return REDACTED;
  return JSON.stringify(
    Object.fromEntries(
      Object.entries(json).map(([field, fieldValue]) => [
        field,
        kept.includes(field) ? fieldValue : REDACTED,
      ]),
    ),
  );
}

// Query values can hold source table names, OAuth codes and invite tokens; keys stay so deep links remain visible.
function redactUrl(url: unknown) {
  if (typeof url !== "string") return url;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url.split(/[?#]/)[0];
  }
  const query = new URLSearchParams();
  parsed.searchParams.forEach((value, key) => {
    query.append(key, redactQueryValue(key, value));
  });
  const search = query.toString();
  return parsed.origin + parsed.pathname + (search ? `?${search}` : "");
}

export function sanitizeMixpanelProperties(
  eventName: string,
  properties: Properties,
): Properties {
  const sanitized = { ...properties };
  for (const key of BLOCKED_PROPERTIES) {
    if (key in sanitized) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn(
          `Mixpanel: dropped "${key}" from "${eventName}"; it can carry customer content.`,
        );
      }
      delete sanitized[key];
    }
  }
  for (const key of URL_PROPERTIES) {
    if (key in sanitized) {
      sanitized[key] = redactUrl(sanitized[key]);
    }
  }
  return sanitized;
}
